import DocumentPage from './DocumentPage';

export const facultyDocument = {
  slug: 'faculty',
  route: '/faculty',
  title: 'Faculty',
  category: 'Academics',
  format: 'DOCX',
  description:
    'EduX is a fully integrated application that helps manage faculty profiles, personal information, subjects, salary records, library details, sessions, media movement, and employee creation.',
  metrics: [
    { label: 'Primary focus', value: 'Faculty management' },
    { label: 'Core records', value: 'Profiles, subjects, salary' },
    { label: 'Automation', value: 'Create Employee flow' },
  ],
  workflow: [
    'Go to the Faculties menu and click New to create a new faculty profile.',
    'Fill in the faculty form and maintain the Personal Information, Subjects, Salary Payment, Library, Sessions, and Media details.',
    'Use Create Employee to open the wizard and automatically populate the linked employee field.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Faculty',
      body: [
        'EduX is fully integrated application which provides a facility to manage the faculty information. Go to menu Faculties.',
        'Click on New to create a new faculty profile.',
      ],
      figure: {
        src: '/assets/doc-images/faculty/image1.png',
        alt: 'Faculty list showing title, name, department, blood group, and emergency contact',
        caption:
          'Faculty list view showing a created faculty record with title, name, department, blood group, and emergency contact.',
      },
    },
    {
      id: 'faculty-form-details',
      title: 'Details of Faculty Form',
      body: [
        'The faculty form is used to capture the basic identity details of the faculty before maintaining operational records in the tabs.',
      ],
      figure: {
        src: '/assets/doc-images/faculty/image2.png',
        alt: 'Faculty form with personal information tab and profile details',
        caption:
          'Faculty form showing the photo area, title, faculty name fields, and the Personal Information tab with profile details.',
      },
      bullets: [
        'Photo: you can upload the photograph of a faculty using image widget at the top-left corner.',
        'Title: provide the appropriate title for the faculty from the list of titles.',
        'Name: assign values in First Name, Middle Name and Last Name to specify the name of the faculty.',
      ],
    },
    {
      id: 'pages-in-faculty-form',
      title: 'Pages in Faculty Form',
      body: [
        'The faculty form contains dedicated pages where personal, academic, payroll, library, and activity-related details are maintained.',
      ],
      subsections: [
        {
          id: 'personal-information',
          title: 'Personal Information',
          body: [
            'You can specify the personal details of the faculty under this page.',
          ],
          figure: {
            src: '/assets/doc-images/faculty/image2.png',
            alt: 'Personal information tab showing faculty personal details',
            caption:
              'Personal Information tab showing faculty contact, birth date, nationality, language, blood group, visa information, and emergency contact.',
          },
          bullets: [
            'Basic Information: under Basic Information, provide details like Gender, Phone, Mobile number and Email of the faculty.',
            'Birth Date: specify the birth date of the faculty.',
            'Blood Group: specify the blood group of the faculty.',
            'Nationality: specify the nationality of the faculty.',
            'Visa Info: add visa related textual description in Visa Info field.',
            'Language: you can specify the primary language of the faculty.',
            'Emergency Contact: it is the list of the users in the system. You can specify one from the list who can be contacted in emergency.',
          ],
        },
        {
          id: 'subjects-detail',
          title: 'Subjects Detail',
          body: [
            'You can specify the list of subjects that the faculty teaches under this tab.',
          ],
          figure: {
            src: '/assets/doc-images/faculty/image4.png',
            alt: 'Subjects tab showing subjects taught by the faculty member',
            caption:
              'Subjects tab listing the subjects taught by the faculty together with code, type, subject type, and grade weightage.',
          },
        },
        {
          id: 'salary-payment',
          title: 'Salary Payment',
          body: [
            'Salary Payment Tab',
            'This section manages salary records of faculty.',
            'Purpose: maintains salary payment history of faculty members.',
          ],
          figure: {
            src: '/assets/doc-images/faculty/imagec.png',
            alt: 'Salary payment tab with faculty monthly salary details',
            caption:
              'Salary Payment tab showing month and year, total salary, salary received, and total leaves taken for the faculty.',
          },
          bullets: [
            'Month and Year',
            'Total Salary',
            'Salary Received',
            'Total Leaves Taken',
            'Add monthly salary entries.',
            'Monitor leave deductions.',
          ],
        },
        {
          id: 'library-detail',
          title: 'Library Detail',
          body: [
            'Faculty may have a record in Library management system as they also need to use the library functionality.',
          ],
          figure: {
            src: '/assets/doc-images/faculty/image5.png',
            alt: 'Library tab showing the faculty library card reference',
            caption:
              'Library tab showing the library card linked to the faculty profile.',
          },
        },
        {
          id: 'sessions',
          title: 'Sessions',
          body: [
            'Whenever a session is created for this faculty, record is added under this tab. So you can have quick reference to all the sessions related details of the faculty.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/faculty/image7.png',
              alt: 'Faculty smart buttons showing sessions and media counts',
              caption: 'Smart buttons showing the number of linked Sessions and Media records.',
            },
            {
              src: '/assets/doc-images/faculty/image8.png',
              alt: 'Session list related to the faculty member',
              caption:
                'Sessions list showing the faculty, batch, subject, classroom, start time, end time, and status.',
            },
          ],
        },
        {
          id: 'media',
          title: 'Media',
          body: [
            'The Media section in the Faculty module tracks issued books, due dates, return status, and penalty details for library management.',
          ],
          figure: {
            src: '/assets/doc-images/faculty/imaged.png',
            alt: 'Media movement records linked to the faculty member',
            caption:
              'Media section showing library card, media item, issue date, due date, return details, overdue amount, and penalty values.',
          },
        },
      ],
    },
    {
      id: 'create-employee',
      title: 'Create Employee',
      body: [
        'Clicking on Create Employee button, wizard will be displayed.',
        'When you click on button Create Employee in wizard, value of Employee field is set automatically as shown in image.',
      ],
      figure: {
        src: '/assets/doc-images/faculty/image9.png',
        alt: 'Faculty action menu showing the Create Employee option',
        caption:
          'Faculty profile action menu showing the Create Employee option before opening the wizard.',
      },
      gallery: [
        {
          src: '/assets/doc-images/faculty/image10.png',
          alt: 'Create Employee wizard for the faculty record',
          caption:
            'Create Employee wizard with the option to create a user along with the employee record.',
        },
        {
          src: '/assets/doc-images/faculty/image11.png',
          alt: 'Faculty profile after employee field has been populated',
          caption:
            'Faculty profile after employee creation, showing the HR Employee field filled automatically.',
        },
      ],
      bullets: [
        'Click Create Employee to open the wizard.',
        'Click Create Employee again in the wizard to set the Employee field automatically.',
      ],
    },
  ],
  related: ['hr', 'assignments', 'timetable'],
};

export default function FacultyPage() {
  return <DocumentPage document={facultyDocument} />;
}
